import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/utils/api';
import {
  ArrowLeft,
  Calendar,
  ClipboardCheck,
  Car,
  Building2,
  Users,
  BookOpen,
  FileSearch,
  MapPin,
  Clock,
  Save,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Zap,
  Target,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Loader2,
  Download,
  BarChart3,
  Plane,
  Building,
  Warehouse,
  Server,
  UserX,
  Presentation,
  GraduationCap,
  FileText,
  Shield
} from 'lucide-react';

interface WeeklyData {
  weekStart: string;
  weekEnd: string;
  status: 'draft' | 'submitted' | 'approved';
  
  // Auto-populated from app (shown with ⚡ indicator)
  auditsCompleted: number;
  auditsInProgress: number;
  jhasCreated: number;
  constructionPlansCreated: number;
  
  // Semi-automated (could pull from future modules)
  investigationsEntered: number;
  investigationsHours: number;
  investigationsClosed: number;
  
  // Manual entry with future automation potential
  officesVisited: number;
  warehousesAudited: number;
  dataCentersVisited: number;
  milesDriven: number;
  noShowSites: number;
  
  // Meetings & Training
  meetingsAttended: number;
  safetyMeetingsLed: number;
  rooftopPlansWorked: number;
  helicopterPlansWorked: number;
  trainingClassesLed: number;
  trainingClassesTaken: number;
  
  // Notes
  notes: string;
  accomplishments: string;
  challenges: string;
  nextWeekPriorities: string;
}

const ACTIVITY_CATEGORIES = [
  {
    title: 'Audits & Assessments',
    icon: ClipboardCheck,
    color: 'emerald',
    items: [
      { key: 'auditsCompleted', label: 'Audits Completed', auto: true, icon: CheckCircle, autoSource: 'Safety Audit module' },
      { key: 'auditsInProgress', label: 'Audits In Progress', auto: true, icon: Clock, autoSource: 'Safety Audit module' },
      { key: 'jhasCreated', label: 'JHAs Created', auto: true, icon: FileText, autoSource: 'JHA module' },
      { key: 'constructionPlansCreated', label: 'Construction Plans', auto: true, icon: Target, autoSource: 'CX Plan module' },
    ],
  },
  {
    title: 'Investigations',
    icon: FileSearch,
    color: 'purple',
    items: [
      { key: 'investigationsEntered', label: 'Investigations Entered', auto: false, futureAuto: true, icon: FileSearch, futureSource: 'Investigation module' },
      { key: 'investigationsHours', label: 'Investigation Hours', auto: false, futureAuto: true, type: 'decimal', icon: Clock, futureSource: 'Investigation module time tracking' },
      { key: 'investigationsClosed', label: 'Investigations Closed', auto: false, futureAuto: true, icon: CheckCircle, futureSource: 'Investigation module' },
    ],
  },
  {
    title: 'Site Visits & Travel',
    icon: Car,
    color: 'blue',
    items: [
      { key: 'officesVisited', label: 'Offices Visited', auto: false, futureAuto: true, icon: Building, futureSource: 'GPS geofencing + Sites Locator API' },
      { key: 'warehousesAudited', label: 'Warehouses Audited', auto: false, futureAuto: true, icon: Warehouse, futureSource: 'GPS geofencing + Sites Locator API' },
      { key: 'dataCentersVisited', label: 'Data Centers Visited', auto: false, futureAuto: true, icon: Server, futureSource: 'GPS geofencing + Sites Locator API' },
      { key: 'milesDriven', label: 'Miles Driven', auto: false, futureAuto: true, icon: Car, futureSource: 'GPS mileage tracking' },
      { key: 'noShowSites', label: 'No-Show Sites', auto: false, futureAuto: true, icon: UserX, futureSource: 'Smart detection: <1hr visit + no audit opened' },
    ],
  },
  {
    title: 'Meetings & Training',
    icon: Users,
    color: 'amber',
    items: [
      { key: 'meetingsAttended', label: 'Meetings Attended', auto: false, futureAuto: true, icon: Users, futureSource: 'Teams/Outlook calendar' },
      { key: 'safetyMeetingsLed', label: 'Safety Meetings Led', auto: false, futureAuto: true, icon: Shield, futureSource: 'Teams/Outlook calendar (organizer)' },
      { key: 'trainingClassesLed', label: 'Training Classes Led', auto: false, futureAuto: true, icon: Presentation, futureSource: 'Training module' },
      { key: 'trainingClassesTaken', label: 'Training Classes Taken', auto: false, futureAuto: true, icon: GraduationCap, futureSource: 'Training module' },
    ],
  },
  {
    title: 'Specialized Plans',
    icon: FileText,
    color: 'rose',
    items: [
      { key: 'rooftopPlansWorked', label: 'Rooftop Safety Plans', auto: false, futureAuto: true, icon: Building2, futureSource: 'Calendar (virtual) + manual (in-person)' },
      { key: 'helicopterPlansWorked', label: 'Helicopter Plans', auto: false, futureAuto: true, icon: Plane, futureSource: 'Calendar (virtual) + manual (in-person)' },
    ],
  },
];

const getColorClasses = (color: string) => {
  const colors: Record<string, { bg: string; border: string; text: string; light: string }> = {
    emerald: { bg: 'bg-emerald-900/30', border: 'border-emerald-600', text: 'text-emerald-400', light: 'bg-emerald-500/20' },
    purple: { bg: 'bg-purple-900/30', border: 'border-purple-600', text: 'text-purple-400', light: 'bg-purple-500/20' },
    blue: { bg: 'bg-blue-900/30', border: 'border-blue-600', text: 'text-blue-400', light: 'bg-blue-500/20' },
    amber: { bg: 'bg-amber-900/30', border: 'border-amber-600', text: 'text-amber-400', light: 'bg-amber-500/20' },
    rose: { bg: 'bg-rose-900/30', border: 'border-rose-600', text: 'text-rose-400', light: 'bg-rose-500/20' },
  };
  return colors[color] || colors.emerald;
};

export function WeeklyActivityPage() {
  const [currentWeek, setCurrentWeek] = useState(() => {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    return monday;
  });
  
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const weekKey = `${currentWeek.getFullYear()}-${String(currentWeek.getMonth() + 1).padStart(2, '0')}-${String(currentWeek.getDate()).padStart(2, '0')}`;
  const [showAutomationInfo, setShowAutomationInfo] = useState(false);
  
  const [weeklyData, setWeeklyData] = useState<WeeklyData>({
    weekStart: '',
    weekEnd: '',
    status: 'draft',
    auditsCompleted: 0,
    auditsInProgress: 0,
    jhasCreated: 0,
    constructionPlansCreated: 0,
    investigationsEntered: 0,
    investigationsHours: 0,
    investigationsClosed: 0,
    officesVisited: 0,
    warehousesAudited: 0,
    dataCentersVisited: 0,
    milesDriven: 0,
    noShowSites: 0,
    meetingsAttended: 0,
    safetyMeetingsLed: 0,
    rooftopPlansWorked: 0,
    helicopterPlansWorked: 0,
    trainingClassesLed: 0,
    trainingClassesTaken: 0,
    notes: '',
    accomplishments: '',
    challenges: '',
    nextWeekPriorities: '',
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(''); setLoadError(false);
    api.get('/weekly-reports/mine', { params: { weekStart: weekKey } }).then(res => {
      if (cancelled) return;
      const report = res.data.data;
      setWeeklyData({ ...report.data, weekStart: weekKey, weekEnd: '', status: report.status });
      setLastSaved(report.updatedAt || null);
    }).catch(err => { if (!cancelled) { setLoadError(true); setError(err.response?.data?.message || 'Unable to load this week. Please retry.'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [weekKey]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => { if (dirty) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const navigateWeek = (direction: 'prev' | 'next') => {
    if (dirty && !window.confirm('Discard unsaved changes to this week?')) return;
    setDirty(false);
    setCurrentWeek(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
      return newDate;
    });
  };

  const formatWeekRange = () => {
    const weekEnd = new Date(currentWeek);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    return `${currentWeek.toLocaleDateString('en-US', options)} - ${weekEnd.toLocaleDateString('en-US', { ...options, year: 'numeric' })}`;
  };

  const handleInputChange = (key: string, value: number | string) => {
    setDirty(true);
    setWeeklyData(prev => ({ ...prev, [key]: value }));
  };

  const saveReport = async (status: 'draft' | 'submitted') => {
    setSaving(true); setError('');
    try {
      const res = await api.put('/weekly-reports/mine', { weekStart: weekKey, status, data: weeklyData });
      const report = res.data.data;
      setWeeklyData({ ...report.data, weekStart: weekKey, weekEnd: '', status: report.status });
      setLastSaved(report.updatedAt); setDirty(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Report was not saved. Please retry.');
    } finally { setSaving(false); }
  };
  const handleSave = () => saveReport('draft');
  const handleSubmit = () => saveReport('submitted');

  // Calculate totals for summary
  const totalActivities = 
    weeklyData.auditsCompleted + 
    weeklyData.jhasCreated + 
    weeklyData.investigationsEntered +
    weeklyData.officesVisited +
    weeklyData.warehousesAudited +
    weeklyData.dataCentersVisited +
    weeklyData.meetingsAttended +
    weeklyData.safetyMeetingsLed +
    weeklyData.trainingClassesLed +
    weeklyData.trainingClassesTaken;

  if (loading) return <div className="p-8" role="status">Loading weekly report…</div>;
  if (loadError) return <div className="p-8"><p role="alert">{error}</p><button onClick={() => window.location.reload()}>Retry</button><Link to="/dashboard">Back to dashboard</Link></div>;
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {error && <p role="alert" className="p-4 bg-red-100 text-red-900">{error}</p>}
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="container mx-auto px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-slate-400 hover:text-white">
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <BarChart3 className="h-6 w-6 text-emerald-400" />
                  Weekly Activity Report
                </h1>
                <p className="text-sm text-slate-400">Track and report your weekly activities</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setShowAutomationInfo(!showAutomationInfo)}
                className="px-4 py-2 bg-purple-900/30 text-purple-400 rounded-lg hover:bg-purple-900/50 flex items-center gap-2 border border-purple-700"
              >
                <Sparkles className="h-4 w-4" />
                Automation Info
              </button>
              <button onClick={() => window.print()} className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 flex items-center gap-2">
                <Download className="h-4 w-4" />
                Export
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Automation Info Banner */}
      {showAutomationInfo && (
        <div className="bg-purple-900/20 border-b border-purple-700">
          <div className="container mx-auto px-6 py-6">
            <div className="flex items-start gap-4">
              <Sparkles className="h-6 w-6 text-purple-400 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-purple-300 mb-3">Automation Roadmap - Potential ~90% Auto-Population</h3>
                <div className="grid md:grid-cols-3 gap-6 text-sm">
                  <div>
                    <p className="text-emerald-400 font-medium mb-2 flex items-center gap-1">
                      <Zap className="h-4 w-4" /> Currently Auto-Populated
                    </p>
                    <ul className="text-slate-300 space-y-1.5">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-3 w-3 text-emerald-500 mt-1 flex-shrink-0" />
                        <span>Audits from Safety Audit module</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-3 w-3 text-emerald-500 mt-1 flex-shrink-0" />
                        <span>JHAs from JHA module</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-3 w-3 text-emerald-500 mt-1 flex-shrink-0" />
                        <span>Construction Plans from CX Plan module</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-blue-400 font-medium mb-2 flex items-center gap-1">
                      <MapPin className="h-4 w-4" /> GPS & Location Integration
                    </p>
                    <ul className="text-slate-300 space-y-1.5">
                      <li className="flex items-start gap-2">
                        <Clock className="h-3 w-3 text-blue-400 mt-1 flex-shrink-0" />
                        <span>Miles driven via GPS tracking</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Clock className="h-3 w-3 text-blue-400 mt-1 flex-shrink-0" />
                        <span>Site visits via Sites Locator API + geofencing</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Clock className="h-3 w-3 text-blue-400 mt-1 flex-shrink-0" />
                        <span>No-shows: auto-detect &lt;1hr visit with no audit opened</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-amber-400 font-medium mb-2 flex items-center gap-1">
                      <Calendar className="h-4 w-4" /> Calendar & Module Integration
                    </p>
                    <ul className="text-slate-300 space-y-1.5">
                      <li className="flex items-start gap-2">
                        <Clock className="h-3 w-3 text-amber-400 mt-1 flex-shrink-0" />
                        <span>Meetings from Teams/Outlook calendar</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Clock className="h-3 w-3 text-amber-400 mt-1 flex-shrink-0" />
                        <span>Safety meetings (organizer = "led")</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Clock className="h-3 w-3 text-amber-400 mt-1 flex-shrink-0" />
                        <span>Investigations from future module</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Clock className="h-3 w-3 text-amber-400 mt-1 flex-shrink-0" />
                        <span>Training from LMS module</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <p className="mt-4 text-xs text-slate-400 italic">
                  💡 In-person meetings (rooftop/helicopter plans) would still need manual entry unless logged in a shared calendar
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-6 py-8">
        {/* Week Navigation */}
        <div className="flex items-center justify-between mb-8">
          <button
            disabled={saving}
            onClick={() => navigateWeek('prev')}
            className="p-2 bg-slate-700 rounded-lg hover:bg-slate-600 text-white"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2">
              <Calendar className="h-5 w-5 text-emerald-400" />
              <span className="text-xl font-bold text-white">{formatWeekRange()}</span>
            </div>
            <span className={`text-sm px-3 py-1 rounded-full ${
              weeklyData.status === 'approved' ? 'bg-green-900/30 text-green-400' :
              weeklyData.status === 'submitted' ? 'bg-blue-900/30 text-blue-400' :
              'bg-slate-700 text-slate-400'
            }`}>
              {weeklyData.status.charAt(0).toUpperCase() + weeklyData.status.slice(1)}
            </span>
          </div>
          <button
            disabled={saving}
            onClick={() => navigateWeek('next')}
            className="p-2 bg-slate-700 rounded-lg hover:bg-slate-600 text-white"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
              <Target className="h-4 w-4" />
              Total Activities
            </div>
            <p className="text-3xl font-bold text-white">{totalActivities}</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
              <ClipboardCheck className="h-4 w-4" />
              Audits
            </div>
            <p className="text-3xl font-bold text-emerald-400">{weeklyData.auditsCompleted}</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
              <Car className="h-4 w-4" />
              Miles Driven
            </div>
            <p className="text-3xl font-bold text-blue-400">{weeklyData.milesDriven}</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
              <Clock className="h-4 w-4" />
              Investigation Hours
            </div>
            <p className="text-3xl font-bold text-purple-400">{weeklyData.investigationsHours}</p>
          </div>
        </div>

        {/* Activity Categories */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {ACTIVITY_CATEGORIES.map(category => {
            const colors = getColorClasses(category.color);
            const CategoryIcon = category.icon;
            
            return (
              <div key={category.title} className={`rounded-xl border ${colors.border} ${colors.bg} p-6`}>
                <div className="flex items-center gap-2 mb-4">
                  <CategoryIcon className={`h-5 w-5 ${colors.text}`} />
                  <h2 className="font-bold text-white">{category.title}</h2>
                </div>
                
                <div className="space-y-3">
                  {category.items.map(item => {
                    const ItemIcon = item.icon;
                    return (
                      <div key={item.key} className="flex items-center gap-3">
                        <ItemIcon className="h-4 w-4 text-slate-400" />
                        <label className="flex-1 text-sm text-slate-300 flex items-center gap-2">
                          {item.label}
                          {item.auto && (
                            <span className="flex items-center gap-1 px-1.5 py-0.5 bg-emerald-900/50 text-emerald-400 rounded text-xs">
                              <Zap className="h-3 w-3" />
                              Auto
                            </span>
                          )}
                          {item.futureAuto && !item.auto && (
                            <span className="flex items-center gap-1 px-1.5 py-0.5 bg-purple-900/50 text-purple-400 rounded text-xs">
                              <Sparkles className="h-3 w-3" />
                              Future
                            </span>
                          )}
                        </label>
                        <input
                          type="number"
                          step={item.type === 'decimal' ? '0.5' : '1'}
                          min="0"
                          value={(weeklyData as any)[item.key]}
                          onChange={(e) => handleInputChange(item.key, parseFloat(e.target.value) || 0)}
                          disabled={item.auto || saving || weeklyData.status !== 'draft'}
                          className={`w-20 px-3 py-2 rounded-lg text-white text-center font-medium ${
                            item.auto 
                              ? 'bg-slate-600/50 cursor-not-allowed' 
                              : 'bg-slate-700 border border-slate-600'
                          }`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Notes Section */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6 mb-8">
          <h2 className="font-bold text-white mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-slate-400" />
            Weekly Notes
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Key Accomplishments
              </label>
              <textarea
                value={weeklyData.accomplishments}
                disabled={saving || weeklyData.status !== 'draft'}
                onChange={(e) => handleInputChange('accomplishments', e.target.value)}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                rows={3}
                placeholder="What did you accomplish this week?"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Challenges Faced
              </label>
              <textarea
                value={weeklyData.challenges}
                disabled={saving || weeklyData.status !== 'draft'}
                onChange={(e) => handleInputChange('challenges', e.target.value)}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                rows={3}
                placeholder="Any challenges or blockers?"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Next Week Priorities
              </label>
              <textarea
                value={weeklyData.nextWeekPriorities}
                disabled={saving || weeklyData.status !== 'draft'}
                onChange={(e) => handleInputChange('nextWeekPriorities', e.target.value)}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                rows={2}
                placeholder="What are your priorities for next week?"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Additional Notes
              </label>
              <textarea
                value={weeklyData.notes}
                disabled={saving || weeklyData.status !== 'draft'}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                rows={2}
                placeholder="Any other notes for this week..."
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Last saved: {lastSaved ? new Date(lastSaved).toLocaleString() : 'Not saved yet'}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving || weeklyData.status !== 'draft'}
              className="px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600 flex items-center gap-2"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Draft
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || weeklyData.status !== 'draft'}
              className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
              Submit for Approval
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

